import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2, Building2, Users } from 'lucide-react';
import { toast } from 'sonner';

export const SuperAdminEmpresasPage = () => {
  const [empresas, setEmpresas] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentEmpresa, setCurrentEmpresa] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    direccion: '',
    telefono: '',
    email: ''
  });

  useEffect(() => {
    loadEmpresas();
  }, []);

  const loadEmpresas = async () => {
    try {
      const response = await api.get('/superadmin/empresas');
      setEmpresas(response.data);
    } catch (error) {
      toast.error('Error al cargar empresas');
    }
  };

  const handleOpenDialog = (empresa = null) => {
    if (empresa) {
      setCurrentEmpresa(empresa);
      setFormData({
        nombre: empresa.nombre,
        direccion: empresa.direccion || '',
        telefono: empresa.telefono || '',
        email: empresa.email || ''
      });
    } else {
      setCurrentEmpresa(null);
      setFormData({ nombre: '', direccion: '', telefono: '', email: '' });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currentEmpresa) {
        await api.put(`/superadmin/empresas/${currentEmpresa.id}`, formData);
        toast.success('Empresa actualizada');
      } else {
        await api.post('/superadmin/empresas', formData);
        toast.success('Empresa de transporte creada');
      }
      await loadEmpresas();
      setShowDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/superadmin/empresas/${currentEmpresa.id}`);
      toast.success('Empresa eliminada');
      await loadEmpresas();
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar empresa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="superadmin-empresas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Empresas de Transporte</h1>
          <p className="text-gray-600 mt-1">Gestión de empresas que usan el sistema</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="create-empresa-button">
          <Plus size={18} className="mr-2" />
          Nueva Empresa
        </Button>
      </div>

      <Card>
        <CardContent className="p-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empresa</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>Administradores</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {empresas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                    No hay empresas registradas
                  </TableCell>
                </TableRow>
              ) : (
                empresas.map((empresa) => (
                  <TableRow key={empresa.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                          <Building2 className="text-orange-600" size={20} />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{empresa.nombre}</p>
                          <p className="text-sm text-gray-500">{empresa.direccion || 'Sin dirección'}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{empresa.telefono || '-'}</p>
                        <p className="text-gray-500">{empresa.email || '-'}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="gap-1">
                        <Users size={14} />
                        {empresa.admin_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(empresa)}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setCurrentEmpresa(empresa);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 size={16} className="text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog Crear/Editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {currentEmpresa ? 'Editar Empresa' : 'Nueva Empresa de Transporte'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="nombre">Nombre de la Empresa *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                required
                placeholder="Ej: TransLogic SpA"
              />
            </div>

            <div>
              <Label htmlFor="direccion">Dirección</Label>
              <Input
                id="direccion"
                value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                placeholder="Ej: Av. Principal 123, Santiago"
              />
            </div>

            <div>
              <Label htmlFor="telefono">Teléfono</Label>
              <Input
                id="telefono"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                placeholder="Ej: +56 2 1234 5678"
              />
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Ej: contacto@empresa.cl"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Eliminar */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Eliminación</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de eliminar la empresa <strong>{currentEmpresa?.nombre}</strong>?
              Esto también eliminará sus administradores y todos los datos asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
